/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/types/risk-observation.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import { useQuery } from '@apollo/client/react';
import { NamedItemType } from 'types/graphql-global-types';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import { getFieldChangeItems, getProcessedFieldChangeItems } from 'views/pages/admin/audit-log/service';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { getNamedItemsByType } from '@queries/generated/common.generated';
import { riskObservationsRisks_OpraQueries_riskObservations_RiskObservation } from '@queries/generated/legacy-risk-assessment.generated';

const RiskObservationRecordChangeGrid = ({ oldRecord, newRecord, oldItem, newItem, record }: any) => {
    const refactoredOldRecord = {
        ...oldRecord,
        Description: null,
        Observation: oldRecord.Description,
        Deleted: oldRecord.D === undefined ? newRecord.D : oldRecord.D,
    };
    const refactoredNewRecord = {
        ...newRecord,
        Description: null,
        Observation: newRecord.Description,
        Deleted: newRecord.D,
    };

    const lookups: any = {
        ResponsibleFunctionId: (observation: riskObservationsRisks_OpraQueries_riskObservations_RiskObservation) =>
            observation?.responsibleFunction?.name ?? '',
        ClassificationId: (observation: riskObservationsRisks_OpraQueries_riskObservations_RiskObservation) =>
            observation?.classification?.name ?? '',
        OwnerId: (observation: riskObservationsRisks_OpraQueries_riskObservations_RiskObservation) =>
            observation?.owner ? `${observation?.owner?.firstName} ${observation?.owner?.lastName}` : '',
    };

    const associations: any = {
        ActionIds: {
            itemField: 'actions',
            name: 'Action',
            nameResolver: (action: any) => action.description,
        },
    };

    const { data: vendorData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: {
            type: NamedItemType.VENDOR,
            timestamp: record.created,
        },
        fetchPolicy: 'no-cache',
    });

    const formatCountries = (recordData: { Code: string; Name: string }, index: number) => {
        if (!recordData) {
            return null;
        }

        return `Order: ${index + 1}\nName: ${recordData?.Name}`;
    };

    const formatVendorIds = (recordData: any, index: number) => {
        if (!recordData) {
            return null;
        }

        const data = recordData as { $oid: string };
        const vendor = vendorData?.namedItemsByType?.find((rv) => rv?.id === data.$oid);

        if (!vendor) {
            return null;
        }

        return `Order: ${index + 1}\n RecordId: ${vendor?.recordId}\nName: ${vendor?.name}`;
    };

    const arrayOfObjectsV2: any = {
        Countries: {
            valueResolver: formatCountries,
            compareField: 'Code',
        },
        VendorIds: {
            valueResolver: formatVendorIds,
            compareField: '$oid',
        },
    };

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            lookups,
            associations,
            arrayOfObjectsV2,
        }),
    );

    return <FieldChangeGrid items={items} />;
};
export { RiskObservationRecordChangeGrid };
